"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
exports.scanDescriptors = exports.inferDescriptorFromDeviceInfo = exports.inferDescriptorFromAccount = void 0;
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
var invariant_1 = __importDefault(require("invariant"));
var bs58_1 = __importDefault(require("bs58"));
var crypto_1 = require("../../crypto");
var derivation_1 = require("../../derivation");
var deviceAccess_1 = require("../../hw/deviceAccess");
var getAddress_1 = __importDefault(require("../../hw/getAddress"));
var perDerivation = {
    "": function (fragment) { return "pkh(".concat(fragment, ")"); },
    segwit: function (fragment) { return "sh(wpkh(".concat(fragment, "))"); },
    native_segwit: function (fragment) { return "wpkh(".concat(fragment, ")"); }
};
function makeFingerprint(compressedPubKey) {
    return (0, crypto_1.hash160)(compressedPubKey).slice(0, 4);
}
function makeDescriptor(_a) {
    var currency = _a.currency, index = _a.index, derivationMode = _a.derivationMode, fingerprint = _a.fingerprint, xpub = _a.xpub;
    var tmpl = perDerivation[derivationMode];
    if (!tmpl || !xpub)
        return;
    var keyOrigin = fingerprint.toString("hex");
    var scheme = (0, derivation_1.getDerivationScheme)({
        currency: currency,
        derivationMode: derivationMode
    });
    var accountPath = (0, derivation_1.runAccountDerivationScheme)(scheme, currency, {
        account: index
    });
    return {
        external: tmpl("[".concat(keyOrigin, "/").concat(accountPath, "]").concat(xpub, "/0/*")),
        internal: tmpl("[".concat(keyOrigin, "/").concat(accountPath, "]").concat(xpub, "/1/*"))
    };
}
function inferDescriptorFromAccount(account) {
    if (account.currency.family !== "bitcoin")
        return;
    var xpub = account.xpub, derivationMode = account.derivationMode, seedIdentifier = account.seedIdentifier, currency = account.currency, index = account.index;
    var fingerprint = makeFingerprint(compressPublicKeySECP256(Buffer.from(seedIdentifier, "hex")));
    return makeDescriptor({
        derivationMode: derivationMode,
        currency: currency,
        fingerprint: fingerprint,
        xpub: xpub,
        index: index
    });
}
exports.inferDescriptorFromAccount = inferDescriptorFromAccount;
function asBufferUInt32BE(n) {
    var buf = Buffer.allocUnsafe(4);
    buf.writeUInt32BE(n, 0);
    return buf;
}
var compressPublicKeySECP256 = function (publicKey) {
    return Buffer.concat([
        Buffer.from([0x02 + (publicKey[64] & 0x01)]),
        publicKey.slice(1, 33),
    ]);
};
function makeXpub(_a) {
    var version = _a.version, depth = _a.depth, parentFingerprint = _a.parentFingerprint, index = _a.index, chainCode = _a.chainCode, pubKey = _a.pubKey;
    var indexBuffer = asBufferUInt32BE(index);
    indexBuffer[0] |= 0x80;
    var extendedKeyBytes = Buffer.concat([
        asBufferUInt32BE(version),
        Buffer.from([depth]),
        parentFingerprint,
        indexBuffer,
        chainCode,
        pubKey,
    ]);
    var checksum = (0, crypto_1.hash256)(extendedKeyBytes).slice(0, 4);
    return bs58_1["default"].encode(Buffer.concat([extendedKeyBytes, checksum]));
}
function inferDescriptorFromDeviceInfo(_a) {
    var derivationMode = _a.derivationMode, currency = _a.currency, index = _a.index, parentDerivation = _a.parentDerivation, accountDerivation = _a.accountDerivation;
    (0, invariant_1["default"])(currency.bitcoinLikeInfo, "bitcoin currency expected");
    var bitcoinLikeInfo = currency.bitcoinLikeInfo;
    var XPUBVersion = bitcoinLikeInfo.XPUBVersion;
    (0, invariant_1["default"])(XPUBVersion, "unsupported bitcoin fork %s", currency.id);
    var chainCode = accountDerivation.chainCode;
    (0, invariant_1["default"])(chainCode, "chainCode is required");
    var fingerprint = makeFingerprint(compressPublicKeySECP256(Buffer.from(parentDerivation.publicKey, "hex")));
    var xpub = makeXpub({
        version: XPUBVersion,
        depth: 3,
        parentFingerprint: fingerprint,
        index: index,
        chainCode: Buffer.from(chainCode, "hex"),
        pubKey: compressPublicKeySECP256(Buffer.from(accountDerivation.publicKey, "hex"))
    });
    return makeDescriptor({
        derivationMode: derivationMode,
        currency: currency,
        fingerprint: fingerprint,
        xpub: xpub,
        index: index
    });
}
exports.inferDescriptorFromDeviceInfo = inferDescriptorFromDeviceInfo;
function scanDescriptors(deviceId, currency, limit) {
    if (limit === void 0) { limit = 10; }
    var derivateAddress = function (opts) {
        return (0, deviceAccess_1.withDevice)(deviceId)(function (transport) { return (0, rxjs_1.from)((0, getAddress_1["default"])(transport, opts)); });
    };
    function stepAddress(_a) {
        var index = _a.index, accountDerivation = _a.accountDerivation, parentDerivation = _a.parentDerivation, derivationMode = _a.derivationMode;
        var result = inferDescriptorFromDeviceInfo({
            derivationMode: derivationMode,
            currency: currency,
            index: index,
            parentDerivation: parentDerivation,
            accountDerivation: accountDerivation
        });
        return !result
            ? rxjs_1.EMPTY
            : (0, rxjs_1.of)({
                result: result,
                complete: index >= limit
            });
    }
    return (0, rxjs_1.from)((0, derivation_1.getDerivationModesForCurrency)(currency)).pipe((0, operators_1.concatMap)(function (derivationMode) {
        return (0, derivation_1.walletDerivation)({
            currency: currency,
            derivationMode: derivationMode,
            derivateAddress: derivateAddress,
            stepAddress: stepAddress,
            shouldDerivesOnAccount: true
        });
    }));
}
exports.scanDescriptors = scanDescriptors;
//# sourceMappingURL=descriptor.js.map